/**
 * AttributesMapSection Component
 *
 * Displays asset attributes (IP, cloud resources) with a geographic visualization.
 * Shows attributes like PTR records, country, AS information, ARN, region, etc.
 * Includes WorldMap visualization highlighting countries where the asset is located.
 */

import { WorldMap } from '@/components/charts/WorldMap';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { OverflowText } from '@/components/OverflowText';

export type OverviewTabAttributeList =
  | 'ptr'
  | 'country'
  | 'asname'
  | 'asnumber'
  | 'asrange'
  | 'ipaddress'
  | 'arn'
  | 'name'
  | 'resourceType'
  | 'region'
  | 'account'
  | 'subscription'
  | 'project';

/**
 * Human-readable display names for attribute types
 */
const AttributeNameMap: Record<OverviewTabAttributeList, string> = {
  ptr: 'Reverse Lookup',
  country: 'Country',
  asname: 'AS Name',
  asnumber: 'AS Number',
  asrange: 'AS Range',
  ipaddress: 'IP Address',
  arn: 'ARN',
  name: 'Name',
  resourceType: 'Resource Type',
  region: 'Region',
  account: 'Account',
  subscription: 'Subscription',
  project: 'Project',
};

export interface AttributesMapSectionProps {
  attributesByName: Record<
    OverviewTabAttributeList,
    { value: string | number }[]
  >;
  isAssetIdentifierAnIpAddress: boolean;
  isAssetIdentifierACloudResource: boolean;
}

export function AttributesMapSection({
  attributesByName,
  isAssetIdentifierAnIpAddress,
  isAssetIdentifierACloudResource,
}: AttributesMapSectionProps) {
  return (
    <div className="flex min-h-64 flex-auto gap-6 overflow-hidden">
      <div className="flex w-1/3 shrink-0 flex-col gap-6">
        {Object.entries(attributesByName)
          .filter(
            ([, attributes]) =>
              attributes.length > 0 &&
              attributes.every(attribute => attribute.value)
          )
          .map(([label, attributes], index) => {
            return (
              <div key={index} className="flex flex-col gap-1">
                <h1 className="text-lg font-bold">
                  {AttributeNameMap[label as OverviewTabAttributeList]}
                </h1>
                {attributes.length === 0 &&
                  (isAssetIdentifierAnIpAddress
                    ? '-'
                    : isAssetIdentifierACloudResource
                      ? '-'
                      : 'No IP address linked to this asset')}
                {attributes.map((attribute, index) => {
                  return (
                    <CopyToClipboard
                      key={index}
                      textToCopy={attribute.value.toString()}
                      alwaysShowCopyIcon
                    >
                      <OverflowText
                        text={attribute.value.toString()}
                        className="text-nowrap"
                      />
                    </CopyToClipboard>
                  );
                })}
              </div>
            );
          })}
      </div>
      <div className="w-full overflow-hidden">
        <WorldMap
          baseClassName="self-start"
          className="fill-chariot-stroke"
          highlightedClassName="dark:fill-chariot-brand-primary"
          countries={Object.fromEntries(
            attributesByName.country.map(attr => [attr.value, 1])
          )}
        />
      </div>
    </div>
  );
}
